import { useEffect, useState } from "react";
import api from "../../utils/api";

// Shared building blocks for the order popups (NewOrderAlert, OrderPopup) so
// both show the same details with the same look.

// Compact button size so footer buttons line up
export const dialogBtn =
  "h-9 px-4 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap";

export const dialogSelect =
  "w-full h-9 px-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-400 focus:bg-white";

// Totals: saved order values first, else calculated from the rows.
// Pass useSaved=false while the rows are being edited.
export function computeOrderTotals(order, rows, gstFraction, useSaved = true) {
  const gstRate = order?.taxrate ?? gstFraction ?? 0.05;
  const lineTotal = (p) => Number(p.offerPrice || p.price || 0) * Number(p.quantity || 0);

  const calcSub = rows.reduce((sum, p) => sum + lineTotal(p), 0);
  const calcPack = rows.reduce(
    (sum, p) => sum + Number(p.packageCharge || 0) * Number(p.quantity || 0),
    0,
  );
  const calcGst = rows.reduce(
    (sum, p) => (p.isTaxable === false ? sum : sum + lineTotal(p) * gstRate),
    0,
  );

  const subTotal = useSaved ? order?.subTotal ?? calcSub : calcSub;
  const packaging = useSaved ? order?.packagingTotal ?? calcPack : calcPack;
  const gstAmount = useSaved ? order?.taxAmount ?? calcGst : calcGst;

  return {
    gstRate,
    lineTotal,
    subTotal,
    packaging,
    gstAmount,
    total: subTotal + gstAmount + packaging,
    totalItems: rows.reduce((sum, p) => sum + Number(p.quantity || 0), 0),
  };
}

// Header colour per order status (matches the status colours on the Orders page)
const STATUS_HEADER = {
  pending: "from-orange-500 to-amber-500",
  accept: "from-cyan-600 to-teal-500",
  "delivery pickup": "from-blue-600 to-indigo-500",
  "out for delivery": "from-amber-500 to-yellow-500",
  completed: "from-green-600 to-emerald-500",
  reject: "from-red-600 to-rose-500",
};
const DEFAULT_HEADER = "from-gray-700 to-gray-500";

export const OrderDialogShell = ({
  status,
  title,
  subtitle,
  icon,
  pulse = false,
  badge,
  onClose,
  closeLabel = "Close",
  zIndex = 9999,
  footer,
  children,
  labelId,
}) => (
  <div
    className="fixed inset-0 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm sm:p-4"
    style={{ zIndex }}
    role="dialog"
    aria-modal="true"
    aria-labelledby={labelId}
  >
    <div className="w-full sm:max-w-lg max-h-[92vh] flex flex-col bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden">
      <div
        className={`bg-gradient-to-r ${STATUS_HEADER[status] || DEFAULT_HEADER} px-5 pt-5 pb-4 text-white transition-colors`}
      >
        <div className="flex items-center gap-3">
          <div className="relative flex-shrink-0">
            {pulse && <span className="absolute inset-0 rounded-full bg-white/40 animate-ping" />}
            <div className="relative w-11 h-11 rounded-full bg-white/20 flex items-center justify-center text-xl">
              {icon}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h3 id={labelId} className="text-lg font-bold leading-tight">
              {title}
            </h3>
            <p className="text-sm text-white/85 truncate">{subtitle}</p>
          </div>
          {badge}
          <button
            type="button"
            onClick={onClose}
            title={closeLabel}
            aria-label={closeLabel}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-white/80 hover:text-white hover:bg-white/15 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">{children}</div>

      {footer && (
        <div
          className="border-t border-gray-100 bg-white px-5 pt-3"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
        >
          {footer}
        </div>
      )}
    </div>
  </div>
);

export const HeaderBadge = ({ children }) => (
  <span className="px-2.5 py-1 rounded-full bg-white/20 text-white text-xs font-semibold whitespace-nowrap capitalize">
    {children}
  </span>
);

export const InfoTile = ({ label, value, className = "", warn = false }) => (
  <div
    className={`rounded-xl px-3 py-2 border ${
      warn ? "bg-red-50 border-red-100" : "bg-gray-50 border-gray-100"
    } ${className}`}
  >
    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{label}</p>
    <div className={`text-sm font-medium break-words ${warn ? "text-red-600" : "text-gray-900"}`}>
      {value}
    </div>
  </div>
);

// Section with a small uppercase heading, used for driver/reject blocks
export const DialogSection = ({ title, hint, children, tone = "gray" }) => (
  <div
    className={`rounded-2xl border px-4 py-3 space-y-2.5 ${
      tone === "blue" ? "border-blue-100 bg-blue-50/50" : "border-gray-100 bg-gray-50"
    }`}
  >
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">{title}</p>
      {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
    </div>
    {children}
  </div>
);

// Customer tiles + items + bill
export const OrderSummaryDetails = ({
  order,
  rows,
  totals,
  currencySymbol,
  taxLabel,
  extraTiles = null,
}) => {
  const [orderCount, setOrderCount] = useState(null);

  // How many times this customer has ordered
  useEffect(() => {
    if (!order?._id) return;
    if (!order.userId) {
      setOrderCount(1);
      return;
    }
    setOrderCount(null);
    api
      .get(`/orders/count-by-user/${order._id}`)
      .then((res) => setOrderCount(res.data?.count || 1))
      .catch(() => setOrderCount(1));
  }, [order?._id, order?.userId]);

  const money = (n) => `${currencySymbol}${Number(n || 0).toFixed(2)}`;
  const hasAddress = !!(order.address && String(order.address).trim());
  const fullAddress = hasAddress
    ? `${order.address.replace(/,+\s*$/, "")}${order.pincode ? `, ${order.pincode}` : ""}`
    : "No address provided";

  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <InfoTile label="Customer" value={order.name || "Guest"} />
        <InfoTile
          label="Total times"
          value={
            orderCount == null ? (
              "…"
            ) : (
              <span className="flex items-center gap-1.5">
                {orderCount}
                <span
                  className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                    orderCount > 1 ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"
                  }`}
                >
                  {orderCount > 1 ? "REPEAT" : "NEW"}
                </span>
              </span>
            )
          }
        />
        {order.phoneNumber && (
          <InfoTile
            label="Phone"
            value={
              <a href={`tel:${order.phoneNumber}`} className="text-blue-600 hover:underline">
                {order.phoneNumber}
              </a>
            }
          />
        )}
        <InfoTile
          label="Delivery address"
          value={fullAddress}
          className={order.phoneNumber ? "" : "col-span-2"}
          warn={!hasAddress}
        />
        {extraTiles}
      </div>

      {rows.length > 0 && (
        <div className="rounded-2xl border border-gray-100 overflow-hidden">
          <div className="flex justify-between px-4 py-2 bg-gray-50 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
            <span>Order details</span>
            <span>
              {totals.totalItems} {totals.totalItems === 1 ? "item" : "items"}
            </span>
          </div>
          <ul className="divide-y divide-gray-100 m-0 p-0 list-none">
            {rows.map((p, i) => (
              <li key={p._id || i} className="flex items-start gap-3 px-4 py-2.5 m-0">
                <span className="flex-shrink-0 min-w-[28px] h-6 px-1.5 rounded-md bg-orange-50 text-orange-700 text-xs font-bold flex items-center justify-center">
                  {p.quantity}×
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 m-0">
                    {p.productTitle?.split(",")[0] || "Unnamed Product"}
                  </p>
                  {p.addOns?.length > 0 && (
                    <p className="text-xs text-indigo-600 mt-0.5 mb-0">
                      +{" "}
                      {p.addOns
                        .map((a) => (a.quantity > 1 ? `${a.name} x${a.quantity}` : a.name))
                        .join(", ")}
                    </p>
                  )}
                  {p.note && (
                    <p className="mt-1 mb-0 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-2 py-1">
                      📝 {p.note}
                    </p>
                  )}
                </div>
                <span className="flex-shrink-0 text-sm font-semibold text-gray-900">
                  {money(totals.lineTotal(p))}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {rows.length > 0 && (
        <div className="rounded-2xl bg-gray-50 px-4 py-3 space-y-1.5 text-sm">
          <BillRow label="Subtotal" value={money(totals.subTotal)} />
          <BillRow
            label={`${taxLabel} (${(totals.gstRate * 100).toFixed(0)}%)`}
            value={money(totals.gstAmount)}
          />
          {totals.packaging > 0 && <BillRow label="Packaging charges" value={money(totals.packaging)} />}
          <div className="flex justify-between pt-2 mt-1 border-t border-dashed border-gray-300 text-base font-bold text-gray-900">
            <span>Total</span>
            <span>{money(totals.total)}</span>
          </div>
        </div>
      )}
    </>
  );
};

const BillRow = ({ label, value }) => (
  <div className="flex justify-between text-gray-600">
    <span>{label}</span>
    <span className="font-medium text-gray-900">{value}</span>
  </div>
);
